import { ApiError } from '@clockwise/shared';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Download, FileSpreadsheet, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileDropZone } from '@/components/custom/file-drop-zone';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { formatBytes } from '@/lib/format-bytes';
import { cn } from '@/lib/utils';

/**
 * Generic import sheet — drop zone + file picker, mutation wrapper,
 * per-row results panel. Reusable across every importer in the app;
 * domain wrappers (e.g. `ProductImportSheet`) just supply the title,
 * description, the request function, and what to do on success.
 *
 * No domain knowledge lives in here. Result shape is the minimum
 * surface a per-row report needs (counts + per-row outcome + errors);
 * callers can extend `ImportResultRow` with more fields and the report
 * panel will ignore them.
 */

export type ImportRowOutcome = 'created' | 'updated' | 'unchanged' | 'failed';

/**
 * Per-row error. Domains that have moved to the structured shape (a
 * `code` + `message` + optional `field`) get richer rendering — the
 * field name appears as a small chip next to the message, helping
 * users locate the offending column. Older domains still pass plain
 * strings; the panel coerces both shapes to a uniform render.
 */
export type ImportRowErrorInput = string | { code?: string; message: string; field?: string };

/** Minimum shape the report panel needs. Callers can extend with
 *  additional fields — the report ignores them. The panel only renders
 *  the row number and error messages; user-supplied identifiers
 *  (codes/names) are deliberately omitted so the report stays a clean
 *  pointer back into the source file. */
export interface ImportResultRow {
  rowNumber: number;
  outcome: ImportRowOutcome;
  /** Which TAB `rowNumber` counts on, for multi-sheet workbooks. A row number
   *  alone points at four different places in the SOW import, and a failure on
   *  the Retainers tab is not one the operator will find on the SOW tab.
   *  Omitted by single-sheet importers, which then read as before. */
  sheetName?: string;
  errors?: ImportRowErrorInput[];
}

export interface ImportResultBase<R extends ImportResultRow = ImportResultRow> {
  totalRows: number;
  created: number;
  updated: number;
  /**
   * Rows that matched an existing row but were a no-op (the file's
   * values were identical to what's on disk). Optional so older
   * importers that don't distinguish no-ops keep compiling — when
   * absent, the summary panel folds them into Total.
   */
  unchanged?: number;
  failed: number;
  rows: R[];
}

export interface ImportSheetProps<R extends ImportResultRow = ImportResultRow> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  /** Comma-separated `accept` for the file input (default: `.xlsx,.csv`). */
  accept?: string;
  /** Allowed file extensions for the basic client-side check
   *  (default: `['.xlsx', '.csv']`). */
  acceptedExtensions?: string[];
  /**
   * Submitter — turns a File into an import result. The second argument
   * carries a fresh `idempotencyKey` minted for THIS submit; importers
   * whose endpoint honors `Idempotency-Key` should forward it. A new key
   * is generated on every Import click (including after "Import another"),
   * so a second file is never mistaken for a retry of the first and the
   * server won't replay the first file's response. The same key is reused
   * across a TanStack retry of one submit, preserving retry-dedup.
   */
  onImport: (file: File, ctx: { idempotencyKey: string }) => Promise<ImportResultBase<R>>;
  /** Fired after a successful import (e.g. invalidate queries). */
  onSuccess?: (result: ImportResultBase<R>) => void;
  /** Optional template download handler — when supplied, a "Download
   *  template" button appears above the drop zone. */
  onDownloadTemplate?: () => Promise<void> | void;
}

export function ImportSheet<R extends ImportResultRow = ImportResultRow>({
  open,
  onOpenChange,
  title,
  description,
  accept = '.xlsx,.csv',
  acceptedExtensions = ['.xlsx', '.csv'],
  onImport,
  onSuccess,
  onDownloadTemplate,
}: ImportSheetProps<R>) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResultBase<R> | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (vars: { file: File; idempotencyKey: string }) =>
      onImport(vars.file, { idempotencyKey: vars.idempotencyKey }),
    onSuccess: (res) => {
      setResult(res);
      onSuccess?.(res);
      // Toast summary — fold `unchanged` into the success line when
      // present so users see the full picture (e.g. "3 new, 2 updated,
      // 5 unchanged" after a no-op-heavy re-import).
      const unchanged = res.unchanged ?? 0;
      const unchangedNote = unchanged > 0 ? `, ${unchanged} unchanged` : '';
      const summary =
        res.failed === 0
          ? `Imported ${res.created} new, ${res.updated} updated${unchangedNote}`
          : `Imported ${res.created + res.updated} of ${res.totalRows} — ${res.failed} failed${unchangedNote}`;
      if (res.failed === 0) toast.success(summary);
      else toast.warning(summary);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Import failed';
      toast.error(msg);
      // Header-level errors (e.g. missing required column) come back as
      // ApiError.fields; surface them as synthetic failed rows so the
      // panel still gives the user something useful. Count them in
      // `failed` / `totalRows` so the summary tiles agree with the list.
      if (err instanceof ApiError && err.fields) {
        const fieldCount = err.fields.length;
        setResult({
          totalRows: fieldCount,
          created: 0,
          updated: 0,
          failed: fieldCount,
          rows: err.fields.map(
            (f, i) =>
              ({
                rowNumber: i + 1,
                outcome: 'failed' as const,
                errors: [f.message],
              }) as R,
          ),
        });
      }
    },
  });

  const pickFile = (f: File) => {
    setResult(null);
    setFile(f);
  };

  // A staged-but-unimported file is the unsaved work here; once a result is
  // shown the import already ran, so closing is clean.
  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: file !== null && result === null,
    isSubmitting: mutation.isPending,
    onOpenChange,
    title: 'Discard selected file?',
    description: "The file you chose hasn't been imported yet and will be cleared.",
  });

  const fileLooksOk = file
    ? acceptedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    : true;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-3 overflow-auto px-4 py-2">
            {!result && onDownloadTemplate && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownloadTemplate()}
                >
                  <Download />
                  Download template
                </Button>
              </div>
            )}

            {!result && (
              <FileDropZone
                accept={accept}
                icon={<FileSpreadsheet className="size-10 text-muted-foreground" />}
                hint={`or click to browse (${acceptedExtensions.join(', ')})`}
                onFiles={(files) => {
                  const f = files[0];
                  if (f) pickFile(f);
                }}
              >
                {file && (
                  <div className="mt-4 flex w-full max-w-sm items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-left text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileSpreadsheet className="size-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatBytes(file.size)} —{' '}
                          {fileLooksOk ? 'ready' : 'unsupported file type'}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Remove file"
                      onClick={() => setFile(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                )}
              </FileDropZone>
            )}

            {result && <ImportResultView result={result} />}
          </div>

          <SheetFooter className="flex-row justify-end">
            {!result ? (
              <>
                <Button variant="outline" onClick={() => requestOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    file && mutation.mutate({ file, idempotencyKey: crypto.randomUUID() })
                  }
                  disabled={!file || !fileLooksOk || mutation.isPending}
                >
                  {mutation.isPending ? 'Importing…' : 'Import'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setResult(null);
                    setFile(null);
                  }}
                >
                  Import another
                </Button>
                <Button onClick={() => requestOpenChange(false)}>Done</Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

// ---- Result view -----------------------------------------------------------

export function ImportResultView<R extends ImportResultRow>({
  result,
}: {
  result: ImportResultBase<R>;
}) {
  const failures = result.rows.filter((r) => r.outcome === 'failed');
  // Show the Unchanged tile only when the importer reports it — older
  // importers omit the field and fall back to a 4-tile layout, which
  // keeps the existing visual contract for products / service-roles /
  // users until they migrate.
  const hasUnchanged = result.unchanged !== undefined;
  return (
    <div className="space-y-4">
      <div
        className={cn(
          'grid gap-3 text-center text-sm',
          hasUnchanged ? 'grid-cols-5' : 'grid-cols-4',
        )}
      >
        <SummaryStat label="Total" value={result.totalRows} />
        <SummaryStat label="Created" value={result.created} tone="success" />
        <SummaryStat label="Updated" value={result.updated} tone="info" />
        {hasUnchanged ? <SummaryStat label="Unchanged" value={result.unchanged ?? 0} /> : null}
        <SummaryStat
          label="Failed"
          value={result.failed}
          tone={result.failed > 0 ? 'destructive' : undefined}
        />
      </div>

      {failures.length === 0 ? (
        <p className="rounded-md border bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
          Every row imported successfully.
        </p>
      ) : (
        <div className="rounded-md border">
          <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2 text-sm font-medium">
            <AlertCircle className="text-destructive" />
            {failures.length} row{failures.length === 1 ? '' : 's'} failed
          </div>
          <ul className="divide-y">
            {failures.map((row) => (
              <FailureRow key={row.rowNumber} row={row} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FailureRow({ row }: { row: ImportResultRow }) {
  return (
    <li className="px-3 py-2 text-sm">
      <span className="font-mono text-xs text-muted-foreground">
        {row.sheetName ? `${row.sheetName} row ${row.rowNumber}` : `Row ${row.rowNumber}`}
      </span>
      <ul className="mt-1 space-y-1 text-xs text-destructive">
        {(row.errors ?? []).map((e, i) => {
          // Coerce both shapes — older `string` errors and the
          // structured `{ code, message, field }` — to a uniform pair.
          // `i` joins the key with field/code so duplicate messages on
          // the same row still get a stable key.
          const isObj = typeof e === 'object' && e !== null;
          const message = isObj ? e.message : e;
          const field = isObj ? e.field : undefined;
          return (
            <li key={`${i}-${field ?? ''}-${message}`} className="flex items-start gap-2">
              {field ? (
                <span className="mt-[1px] inline-flex shrink-0 items-center rounded bg-destructive/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-destructive">
                  {field}
                </span>
              ) : null}
              <span className="leading-snug">{message}</span>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'info' | 'destructive';
}) {
  const toneClasses =
    tone === 'success'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tone === 'info'
        ? 'text-sky-700 dark:text-sky-300'
        : tone === 'destructive'
          ? 'text-destructive'
          : '';
  return (
    <div className="rounded-md border bg-card px-2 py-3">
      <p className={cn('text-2xl font-semibold tabular-nums', toneClasses)}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
